import {
  Flex,
  Heading,
  IconBulb,
  IconButton,
  IconExclamationCircle,
  Popover,
  Popper,
  Spacer,
  TextButton,
} from '@zillow/constellation';
import type { OverviewStepType } from '../../../models';
import { REVIEW_STATUS, STEP_IDS } from '../../../constants/wizard';
import { LegalNameOverview } from './Sections/1PersonalInfo/1LegalNameOverview';
import type { Answers, UserStatus } from '../../../graphql/queries';
import { ContactOverview } from './Sections/1PersonalInfo/2ContactOverview';
import { OccupantsOverview } from './Sections/2HouseholdInfo/1OccupantsOverview';
import { SmokingOverview } from './Sections/2HouseholdInfo/2SmokingOverview';
import { PetsOverview } from './Sections/2HouseholdInfo/3PetsOverview';
import { MoveInDateOverview } from './Sections/5ResidenceHistory/2MoveInDateOverview';
import { CurrentResidenceOverview } from './Sections/5ResidenceHistory/1CurrentResidenceOverview';
import { CurrentIncomeOverview } from './Sections/4IncomeHistory/1CurrentIncomeOverview';
import { PastResidencesOverview } from './Sections/5ResidenceHistory/3PastResidencesOverview';
import { PastJobsOverview } from './Sections/4IncomeHistory/2PastJobsOverview';
import { PaymentAndReportsOverview } from './Sections/6Reports/1PaymentAndReportsOverview';
import { IDVerificationOverview } from './Sections/3IDVerification/1IDVerificationOverview';
import { PaymentRequestOverview } from '../../ApplicationEdit/Sections/6Reports/2PaymentRequestOverview';

const STEP_COMPONENTS = {
  [STEP_IDS.LEGAL_NAME]: LegalNameOverview,
  [STEP_IDS.CONTACT]: ContactOverview,
  [STEP_IDS.OCCUPANTS]: OccupantsOverview,
  [STEP_IDS.SMOKING]: SmokingOverview,
  [STEP_IDS.PETS]: PetsOverview,
  [STEP_IDS.ID_VERIFICATION]: IDVerificationOverview,
  [STEP_IDS.CURRENT_INCOME]: CurrentIncomeOverview,
  [STEP_IDS.PAST_JOBS]: PastJobsOverview,
  [STEP_IDS.CURRENT_RESIDENCE]: CurrentResidenceOverview,
  [STEP_IDS.MOVE_IN_DATE]: MoveInDateOverview,
  [STEP_IDS.PAST_RESIDENCES]: PastResidencesOverview,
  [STEP_IDS.PAYMENT_AND_REPORTS]: PaymentAndReportsOverview,
  [STEP_IDS.PAYMENT_REQUEST]: PaymentRequestOverview,
};

interface Props {
  applicationId: string;
  isPrimaryApplicant: boolean;
  step: OverviewStepType;
  answers: Answers;
  userStatuses: Array<UserStatus>;
  onClickEdit: () => void;
  isReadOnly?: boolean;
}

export function ApplicationOverviewStep({
  applicationId,
  isPrimaryApplicant,
  step,
  answers,
  userStatuses,
  onClickEdit,
  isReadOnly,
}: Props) {
  const Component = STEP_COMPONENTS[step.id as STEP_IDS];
  const isIncomplete = step.status !== REVIEW_STATUS.COMPLETE;

  const hasErrorIcon = isIncomplete && !isReadOnly;
  const hasRecommendationIcon = !hasErrorIcon && step.recommendation && !isReadOnly;

  const hasIcon = hasErrorIcon || hasRecommendationIcon;

  const RecommendationPopover = (
    <Popover header={<Heading level={6}>{step.recommendation?.title}</Heading>} body={step.recommendation?.body} />
  );

  return Component ? (
    <>
      <Flex display="flex" justifyContent="space-between" alignItems="center" marginTop="xs">
        <Flex display="flex" alignItems="center">
          {hasErrorIcon && <IconExclamationCircle fontColor="yellow600" />}
          {hasRecommendationIcon && (
            <Popper triggered={RecommendationPopover}>
              <IconButton
                aria-label="Step recommendation"
                title="Step recommendation"
                buttonType="secondary"
                type="button"
                appearance="circle"
                bare
                icon={<IconBulb fontColor="brand" />}
                size="xs"
              />
            </Popper>
          )}
          <Heading level={5} as="h4" marginLeft={hasIcon ? (hasRecommendationIcon ? 'xxs' : 'sm') : 'lg'}>
            {step.label}
          </Heading>
          {hasRecommendationIcon && (
            <Popper triggered={RecommendationPopover}>
              <TextButton fontType="bodySmall" marginLeft="xs" type="button">
                Learn more
              </TextButton>
            </Popper>
          )}
        </Flex>
        {!isReadOnly && (
          <TextButton onClick={() => onClickEdit && onClickEdit()} type="button">
            Edit
          </TextButton>
        )}
      </Flex>
      <Spacer marginY="sm" marginLeft="lg">
        <Component
          answers={answers}
          applicationId={applicationId}
          isPrimaryApplicant={isPrimaryApplicant}
          userStatuses={userStatuses}
          isReadOnly={isReadOnly}
        />
      </Spacer>
    </>
  ) : null;
}
